<template>
    <div class="login-container">
        <div class="login-content">
            <!-- Simple Card Form -->
            <div class="signin-card">
                <div class="card-header">
                    <img src="@/assets/images/ams_new_logo.png" alt="AMS Logo" class="logo-image" />
                    <h1 class="card-title">{{ $translate('welcome_back') || 'Welcome Back' }}</h1>
                </div>
                
                <div v-if="signInError" class="alert-error">
                    <i class="pi pi-exclamation-triangle"></i>
                    <span>{{ signInError }}</span>
                </div>
                
                <form @submit.prevent="login" class="signin-form">
                    <div class="field">
                        <label for="email" class="field-label">{{ $translate('username') || 'Username' }}</label>
                        <div class="input-container">
                            <i class="pi pi-user field-icon"></i>
                            <input 
                                id="email"
                                type="email" 
                                v-model="model.email" 
                                class="field-input"
                                :placeholder="$translate('email_placeholder') || 'name@example.com'"
                                required
                            />
                        </div>
                    </div>
                    
                    <div class="field">
                        <label for="password" class="field-label">{{ $translate('password') || 'Password' }}</label>
                        <div class="input-container">
                            <i class="pi pi-lock field-icon"></i>
                            <input 
                                id="password"
                                :type="showPassword ? 'text' : 'password'" 
                                v-model="model.password" 
                                class="field-input with-action"
                                :placeholder="$translate('password_placeholder') || '••••••••'"
                                required
                            />
                            <button 
                                type="button"
                                class="field-action"
                                @click="showPassword = !showPassword"
                                tabindex="-1"
                            >
                                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                            </button>
                        </div>
                    </div>
                    
                    <div class="form-footer">
                        <div class="remember-section">
                            <input type="checkbox" id="remember" class="remember-checkbox" />
                            <label for="remember" class="remember-label">
                                {{ $translate('remember_me') || 'Remember me' }}
                            </label>
                        </div>
                        
                        <router-link to="/forgot_password" class="forgot-password">
                            {{ $translate('forgot') || 'Forgot Password?' }}
                        </router-link>
                    </div>
                    
                    <button 
                        type="submit" 
                        class="signin-button"
                        :disabled="signingIn || loading"
                    >
                        <i v-if="signingIn || loading" class="pi pi-spin pi-spinner"></i>
                        <span v-if="!signingIn && !loading">
                            {{ $translate('sign_in') || 'Sign In' }}
                        </span>
                        <span v-else>
                            {{ $translate('signing_in') || 'Signing in...' }}
                        </span>
                    </button>
                </form>
                
                <div class="divider">
                    <span class="divider-text">OR</span>
                </div>
                
                <div class="signup-section">
                    <span class="signup-text">{{ $translate('dont_have_account') || "Don't have an account?" }}</span>
                    <router-link to="/customer-user" class="signup-link">
                        {{ $translate('sign_up') || 'Sign Up' }}
                    </router-link>
                </div>
                
                <div class="language-selector" @click="showLanguageMenu = !showLanguageMenu">
                    <div class="language-toggle">
                        <span :class="selectedLanguage.flagClass"></span>
                        <span class="language-label">{{ selectedLanguage.label }}</span>
                        <i class="pi pi-chevron-down language-arrow" :class="{ 'rotated': showLanguageMenu }"></i>
                    </div>
                    <div v-if="showLanguageMenu" class="language-menu">
                        <div 
                            v-for="language in languages" 
                            :key="language.id"
                            @click.stop="changeLanguage(language.id)"
                            class="language-option"
                        >
                            <i :class="language.flagClass"></i>
                            <span>{{ language.label }}</span>
                        </div>
                    </div>
                </div>
            </div>
            
        </div>
        
        <!-- Footer Links -->
        <div v-if="termsOfServiceDocumentUrl || privacyPolicyDocumentUrl" class="footer-section">
            <div class="footer-content">
                <a v-if="termsOfServiceDocumentUrl" :href="termsOfServiceDocumentUrl" target="_blank" class="footer-link">
                    {{ $translate('terms_of_service') || 'Terms of Service' }}
                </a>
                <span v-if="termsOfServiceDocumentUrl && privacyPolicyDocumentUrl" class="footer-separator">•</span>
                <a v-if="privacyPolicyDocumentUrl" :href="privacyPolicyDocumentUrl" target="_blank" class="footer-link">
                    {{ $translate('privacy_policy') || 'Privacy Policy' }}
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import layoutMixins from '@/mixins/layoutMixins'

export default {
    name: 'login',
    mixins: [layoutMixins],
    data() {
        return {
            signingIn: false,
            signInError: false,
            model: {
                email: '',
                password: '',
            },
            languages: [
                { id: 'en', label: 'English', flagClass: 'flag-uk' },
                { id: 'es', label: 'Español', flagClass: 'flag-spain' }
            ],
            selectedLanguage: { id: 'en', label: 'English', flagClass: 'flag-uk' },
            showPassword: false,
            loading: false,
            showLanguageMenu: false,
        }
    },
    components: {},
    computed: {
        companySettingData() {
            const data = this.$store.state.company.settings || {}
            return data
        },
        companyPolicyKey() {
            return this.$store.state.company.settingKeys.companyPolicyKey
        },
        companyPolicyValue() {
            const companyPolicy = this.$store.getters['company/companyPolicyValue']
            return companyPolicy
        },
        termsOfServiceDocumentUrl() {
            const url = this.companyPolicyValue && this.companyPolicyValue.terms_of_service
            return url
        },
        privacyPolicyDocumentUrl() {
            const url = this.companyPolicyValue && this.companyPolicyValue.privacy_policy
            return url
        },
    },
    created() {
        this.$store.dispatch('company/fetch')
        this.calcPath()
        document.title = 'AMS - Login'
    },
    mounted() {
        if(localStorage.getItem('session_expired')) {
            const error = localStorage.getItem('session_expired')
            this.signInError = this.$translate(error)
        }
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    watch: {
        '$i18n.locale': {
            immediate: true,
            handler(val) {
                this.selectedLanguage = this.languages.find(language => language.id === val) || this.languages[0];
            }
        },
    },
    methods: {
        handleClickOutside(event) {
            if (!event.target.closest('.language-selector')) {
                this.showLanguageMenu = false
            }
        },
        changeLanguage(langId) {
            if (this.$i18n.locale !== langId) {
                this.$i18n.locale = langId
                localStorage.setItem('language', langId)
            }
            this.showLanguageMenu = false
        },
        async login(e) {
            e.preventDefault()
            this.signingIn = true
            this.loading = true
            this.signInError = false
            
            await this.$store
                .dispatch('login', {
                    email: btoa(this.model.email),
                    password: btoa(this.model.password),
                })
                .then(() => {
                    const redirect = this.$route.query._redirect
                    if (redirect && redirect !== '/' && redirect !== '/login') {
                        this.$router.push(redirect)
                    } else {
                        this.$router.push('dashboard')
                    }
                })
                .catch(error => {
                    this.signInError = error?.response?.data?.error?.message || error.message
                })
                .finally(() => {
                    this.signingIn = false
                    this.loading = false
                })
        },
        calcPath() {
            let pathSplit = this.$route.path.split('/')
            let group = this.$route.meta.group
            let activeGroup = this.$route.meta.activeGroup
            let displayName = this.$route.meta.displayName
            pathSplit.splice(0, 1)

            let paths = []
            let runningPath = ''
            for (let segment of pathSplit) {
                runningPath += '/' + segment
                paths.push({
                    displayName,
                    path: runningPath,
                    text: segment,
                    activeGroup,
                    group,
                })
            }
            this.paths = paths
        },
    },
}
</script>

<style scoped>
@import url('~primeicons/primeicons.css');

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

.login-container {
    min-height: 100vh;
    background: linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%);
    display: flex;
    flex-direction: column;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.login-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
}

/* Signin Card - PrimeBlocks Simple in Card Style */
.signin-card {
    background: white;
    border-radius: 16px;
    padding: 48px 40px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    width: 100%;
    max-width: 440px;
    border: 1px solid #f1f5f9;
}

.card-header {
    text-align: center;
    margin-bottom: 40px;
    background: none;
}

.logo-image {
    height: 48px;
    width: auto;
    margin-bottom: 32px;
}

.card-title {
    font-size: 24px;
    font-weight: 600;
    color: #111827 !important;
    margin: 0 0 8px 0;
    line-height: 1.3;
    background: none !important;
    background-color: transparent !important;
    padding: 0;
    border: none;
    box-shadow: none;
}

.card-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
    line-height: 1.5;
}

.alert-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}

.signin-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.field-label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    line-height: 1.4;
}

.input-container {
    position: relative;
    display: flex;
    align-items: center;
}

.field-icon {
    position: absolute;
    left: 12px;
    color: #9ca3af;
    font-size: 16px;
    z-index: 1;
}

.field-input {
    width: 100%;
    padding: 12px 16px 12px 40px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    color: #111827;
    background: white;
    transition: all 0.2s ease;
    outline: none;
}

.field-input:focus {
    border-color: #2F80ED;
    box-shadow: 0 0 0 3px rgba(47, 128, 237, 0.1);
}

.field-input::placeholder {
    color: #9ca3af;
}

.field-input.with-action {
    padding-right: 40px;
}

.field-action {
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: color 0.2s ease;
}

.field-action:hover {
    color: #6b7280;
}

.form-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: -8px;
}

.remember-section {
    display: flex;
    align-items: center;
    gap: 8px;
}

.remember-checkbox {
    width: 16px;
    height: 16px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
}

.remember-checkbox:checked {
    background: #2F80ED;
    border-color: #2F80ED;
}

.remember-label {
    font-size: 14px;
    color: #6b7280;
    cursor: pointer;
}

.forgot-password {
    font-size: 14px;
    color: #2F80ED;
    text-decoration: none;
    font-weight: 500;
}

.forgot-password:hover {
    color: #56CCF2;
    text-decoration: underline;
}

.signin-button {
    width: 100%;
    padding: 12px 16px;
    background: linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
}

.signin-button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(47, 128, 237, 0.3);
}

.signin-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.divider {
    display: flex;
    align-items: center;
    margin: 24px 0;
}

.divider::before,
.divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
}

.divider-text {
    padding: 0 16px;
    font-size: 12px;
    color: #9ca3af;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.signup-section {
    text-align: center;
    margin-top: 8px;
}

.signup-text {
    font-size: 14px;
    color: #6b7280;
}

.signup-link {
    font-size: 14px;
    color: #2F80ED;
    text-decoration: none;
    font-weight: 600;
    margin-left: 4px;
}

.signup-link:hover {
    color: #56CCF2;
    text-decoration: underline;
}


/* Footer Section */
.footer-section {
    padding: 24px;
    text-align: center;
}

.footer-content {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.footer-link {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color 0.2s ease;
}

.footer-link:hover {
    color: white;
    text-decoration: underline;
}

.footer-separator {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
}

/* Language Selector Styles */
.language-selector {
    position: relative;
    cursor: pointer;
    user-select: none;
    margin-top: 20px;
    align-self: flex-start;
}

.language-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    background-color: white;
    width: 90px;
    font-size: 12px;
    transition: all 0.2s ease;
}

.language-toggle:hover {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
}

.language-label {
    flex: 1;
    color: #374151;
}

.language-arrow {
    font-size: 10px;
    color: #6b7280;
    transition: transform 0.2s ease;
    margin-left: auto;
}

.language-arrow.rotated {
    transform: rotate(180deg);
}

.language-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    z-index: 10;
    margin-top: 4px;
}

.language-option {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    font-size: 12px;
    color: #374151;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.language-option:hover {
    background-color: #f3f4f6;
}

.language-option:first-child {
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
}

.language-option:last-child {
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
}

/* Flag Classes - Using same images as navbar */
.flag-uk, .flag-spain {
    width: 20px;
    height: 14px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 2px;
    display: inline-block;
    flex-shrink: 0;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.flag-uk {
    background-image: url('~@/assets/images/uk-flag.png');
}

.flag-spain {
    background-image: url('~@/assets/images/spain-flag.png');
}

/* Responsive Design */
@media (max-width: 640px) {
    .login-content {
        padding: 20px;
    }
    
    .signin-card {
        padding: 32px 24px;
        margin: 0 16px;
    }
    
    .card-title {
        font-size: 22px;
    }
    
    .signin-form {
        gap: 20px;
    }
    
    .form-footer {
        flex-direction: column;
        gap: 16px;
        align-items: stretch;
        text-align: center;
    }
    
    .footer-content {
        flex-direction: column;
        gap: 12px;
    }
}
</style>